import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import Explore from "@/pages/Explore";
import Resources from "@/pages/Resources";
import Category from "@/pages/Category";
import Article from "@/pages/Article";
import Learning from "@/pages/Learning";
import Finds from "@/pages/Finds";
import Community from "@/pages/Community";
import CommunityStory from "@/pages/CommunityStory";
import ShareStory from "@/pages/ShareStory";
import Privacy from "@/pages/Privacy";
import Guidelines from "@/pages/Guidelines";
import Support from "@/pages/Support";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import ChildSafety from "@/pages/ChildSafety";
import DeleteAccount from "@/pages/DeleteAccount";
import Admin from "@/pages/Admin";
import { applyPublishedResources, applyPublishedFinds, applyPublishedStories } from "@/data/hub";
import { trackPageView } from "@/lib/analytics";

const ScrollToHash = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

// Privacy-safe first-party page-view tracking on every route change.
const RouteTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const t = setTimeout(() => trackPageView(pathname, document.title), 300);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
};

function App() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    let done = false;
    const finish = () => { if (!done) { done = true; setReady(true); } };
    const timer = setTimeout(finish, 2500); // never block render on a slow API
    (async () => {
      try {
        const [r, f] = await Promise.all([
          fetch(`${API}/api/resources`).then((x) => x.json()).catch(() => []),
          fetch(`${API}/api/finds`).then((x) => x.json()).catch(() => []),
        ]);
        if (Array.isArray(r) && r.length) applyPublishedResources(r);
        if (Array.isArray(f) && f.length) applyPublishedFinds(f);
        const s = await fetch(`${API}/api/stories`).then((x) => x.json()).catch(() => []);
        if (Array.isArray(s) && s.length) applyPublishedStories(s);
      } catch (e) { /* keep static content */ }
      clearTimeout(timer);
      finish();
    })();
    return () => clearTimeout(timer);
  }, []);

  if (!ready) return <div className="min-h-screen bg-cream" aria-hidden="true" />;

  return (
    <div className="App">
      <div className="film-grain" aria-hidden="true" />
      <BrowserRouter>
        <ScrollToHash />
        <RouteTracker />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:slug" element={<Category />} />
          <Route path="/read/:slug" element={<Article />} />
          <Route path="/learning" element={<Learning />} />
          <Route path="/finds" element={<Finds />} />
          <Route path="/community" element={<Community />} />
          <Route path="/community-stories/:slug" element={<CommunityStory />} />
          <Route path="/share-your-story" element={<ShareStory />} />
          <Route path="/about" element={<Landing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/community-guidelines" element={<Guidelines />} />
          <Route path="/support" element={<Support />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/child-safety" element={<ChildSafety />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
